import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, BarChart, Bar, ComposedChart, ReferenceLine } from 'recharts';

function StockDetail({ stock, onBack, apiUrl }) {
  const [historicalData, setHistoricalData] = useState(null);
  const [period, setPeriod] = useState('1d');
  const [loading, setLoading] = useState(false);
  const [chartType, setChartType] = useState('line'); // 'line', 'candlestick'

  useEffect(() => {
    if (stock.historical_data && stock.historical_data.length > 0) {
      setHistoricalData(stock.historical_data);
    } else {
      fetchHistoricalData();
    }
  }, [stock.symbol, period]);

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/stocks/${stock.symbol}/history`, {
        params: { period }
      });
      setHistoricalData(response.data.data);
    } catch (err) {
      console.error('Failed to fetch historical data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPredictionColor = (prediction) => {
    if (typeof prediction === 'object') {
      prediction = prediction.prediction || 'neutral';
    }
    switch (prediction) {
      case 'up':
        return '#10b981';
      case 'down':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const mentions = stock.mentions || {};
  const prediction = typeof stock.prediction === 'object' 
    ? stock.prediction 
    : { prediction: stock.prediction, confidence: null };

  // Prepare chart data
  const priceChartData = historicalData?.map(d => ({
    date: d.date,
    price: d.close,
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
    volume: d.volume / 1000000 // Convert to millions
  })) || [];

  const sentimentData = [
    { name: 'Positive', value: mentions.total_positive || 0, color: '#10b981' },
    { name: 'Negative', value: mentions.total_negative || 0, color: '#ef4444' },
    { name: 'Neutral', value: mentions.total_neutral || 0, color: '#6b7280' }
  ].filter(d => d.value > 0);

  const sourceData = [
    { name: 'Reddit', mentions: mentions.reddit?.total || 0 },
    { name: 'Hacker News', mentions: mentions.hackernews?.total || 0 },
    { name: 'News', mentions: mentions.news?.total || 0 }
  ].filter(d => d.mentions > 0);

  return (
    <div className="stock-detail">
      <button className="back-button" onClick={onBack}>
        ← Back to Dashboard
      </button>

      <div className="detail-header">
        <div>
          <h1>{stock.symbol}</h1>
          <h2>{stock.name}</h2>
        </div>
        <div className="prediction-box" style={{ backgroundColor: getPredictionColor(prediction.prediction) }}>
          <div className="prediction-label">Prediction</div>
          <div className="prediction-value">{prediction.prediction.toUpperCase()}</div>
          {prediction.confidence && (
            <div className="prediction-confidence">{prediction.confidence}% confidence</div>
          )}
        </div>
      </div>

      <div className="detail-stats">
        <div className="stat-card">
          <div className="stat-label">Current Price</div>
          <div className="stat-value-large">${stock.current_price?.toFixed(2)}</div>
          <div className={`stat-change ${stock.price_change >= 0 ? 'positive' : 'negative'}`}>
            {stock.price_change >= 0 ? '+' : ''}
            {stock.price_change?.toFixed(2)} ({stock.price_change_percent >= 0 ? '+' : ''}
            {stock.price_change_percent?.toFixed(2)}%)
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Mentions</div>
          <div className="stat-value-large">{mentions.total_mentions || 0}</div>
          <div className="stat-subtext">
            {mentions.total_positive || 0} positive • {mentions.total_negative || 0} negative
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Hype vs Real</div>
          <div className="hype-real-comparison">
            <div className="hype-score">
              <span className="score-label">Hype</span>
              <span className="score-value">{mentions.hype_score || 0}</span>
            </div>
            <div className="real-score">
              <span className="score-label">Real</span>
              <span className="score-value">{mentions.real_score || 0}</span>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Volume</div>
          <div className="stat-value-large">{(stock.volume / 1000000).toFixed(2)}M</div>
        </div>
      </div>

      {prediction.factors && prediction.factors.length > 0 && (
        <div className="factors-box">
          <h3>Prediction Factors</h3>
          <ul>
            {prediction.factors.map((factor, idx) => (
              <li key={idx}>{factor}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="charts-section">
        <div className="chart-container">
            <div className="chart-header">
            <h3>📈 Price Performance</h3>
            <div className="chart-controls">
              <div className="period-selector">
                <button 
                  className={period === '1m' ? 'active' : ''} 
                  onClick={() => setPeriod('1m')}
                  title="1 Minute"
                >1m</button>
                <button 
                  className={period === '5m' ? 'active' : ''} 
                  onClick={() => setPeriod('5m')}
                  title="5 Minutes"
                >5m</button>
                <button 
                  className={period === '15m' ? 'active' : ''} 
                  onClick={() => setPeriod('15m')}
                  title="15 Minutes"
                >15m</button>
                <button 
                  className={period === '30m' ? 'active' : ''} 
                  onClick={() => setPeriod('30m')}
                  title="30 Minutes"
                >30m</button>
                <button 
                  className={period === '1h' ? 'active' : ''} 
                  onClick={() => setPeriod('1h')}
                  title="1 Hour"
                >1h</button>
                <button 
                  className={period === '1d' ? 'active' : ''} 
                  onClick={() => setPeriod('1d')}
                  title="1 Day"
                >1d</button>
                <button 
                  className={period === '7d' ? 'active' : ''} 
                  onClick={() => setPeriod('7d')}
                  title="7 Days"
                >7d</button>
                <button 
                  className={period === '1mo' ? 'active' : ''} 
                  onClick={() => setPeriod('1mo')}
                  title="1 Month"
                >1mo</button>
                <button 
                  className={period === '3mo' ? 'active' : ''} 
                  onClick={() => setPeriod('3mo')}
                  title="3 Months"
                >3mo</button>
                <button 
                  className={period === '1y' ? 'active' : ''} 
                  onClick={() => setPeriod('1y')}
                  title="1 Year"
                >1y</button>
              </div>
              <div className="chart-type-selector">
                <button
                  className={chartType === 'line' ? 'active' : ''}
                  onClick={() => setChartType('line')}
                  title="Line Chart"
                >📈 Line</button>
                <button
                  className={chartType === 'candlestick' ? 'active' : ''}
                  onClick={() => setChartType('candlestick')}
                  title="Candlestick Chart"
                >🕯️ Candles</button>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="chart-loading">Loading chart data...</div>
          ) : priceChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={500}>
              {chartType === 'candlestick' ? (
                <ComposedChart data={priceChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={period.includes('m') || period === '1h' ? -45 : 0}
                    textAnchor={period.includes('m') || period === '1h' ? "end" : "middle"}
                    height={period.includes('m') || period === '1h' ? 80 : 60}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="price" orientation="left" domain={['auto', 'auto']} />
                  <YAxis yAxisId="volume" orientation="right" domain={[0, 'auto']} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '8px' }}
                    formatter={(value, name) => {
                      if (name === 'volume') return `${(value / 1000000).toFixed(2)}M`;
                      return `$${value.toFixed(2)}`;
                    }}
                  />
                  <Legend />
                  {/* OHLC Lines */}
                  <Line yAxisId="price" type="monotone" dataKey="high" stroke="#10b981" strokeWidth={1} dot={false} name="High" />
                  <Line yAxisId="price" type="monotone" dataKey="low" stroke="#ef4444" strokeWidth={1} dot={false} name="Low" />
                  <Line yAxisId="price" type="monotone" dataKey="open" stroke="#667eea" strokeWidth={2} dot={false} strokeDasharray="5 5" name="Open" />
                  <Line yAxisId="price" type="monotone" dataKey="close" stroke="#764ba2" strokeWidth={2} dot={false} name="Close" />
                  <Area yAxisId="volume" type="monotone" dataKey="volume" fill="#9ca3af" fillOpacity={0.2} stroke="#9ca3af" name="Volume (M)" />
                </ComposedChart>
              ) : (
                <AreaChart data={priceChartData}>
                  <defs>
                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#667eea" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#667eea" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={period.includes('m') || period === '1h' ? -45 : 0}
                    textAnchor={period.includes('m') || period === '1h' ? "end" : "middle"}
                    height={period.includes('m') || period === '1h' ? 80 : 60}
                    interval="preserveStartEnd"
                  />
                  <YAxis yAxisId="price" />
                  <YAxis yAxisId="volume" orientation="right" />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}
                    formatter={(value, name) => {
                      if (name === 'volume') return `${(value / 1000000).toFixed(2)}M`;
                      return `$${value.toFixed(2)}`;
                    }}
                  />
                  <Legend />
                  <Area 
                    yAxisId="price"
                    type="monotone" 
                    dataKey="price" 
                    stroke="#667eea" 
                    fillOpacity={1} 
                    fill="url(#colorPrice)" 
                    name="Price ($)"
                  />
                  <Area 
                    yAxisId="volume"
                    type="monotone" 
                    dataKey="volume" 
                    stroke="#9ca3af" 
                    fill="#9ca3af" 
                    fillOpacity={0.2}
                    name="Volume (M)"
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          ) : (
            <div className="no-data">No chart data available for this period</div>
          )}
        </div>

        <div className="charts-grid">
          <div className="chart-container">
            <h3>💬 Sentiment Breakdown</h3>
            {sentimentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sentimentData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#667eea" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No sentiment data available</div>
            )}
          </div>

          <div className="chart-container">
            <h3>📰 Mentions by Source</h3>
            {sourceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sourceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="mentions" fill="#764ba2" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="no-data">No mention data available</div>
            )}
          </div>
        </div>
      </div>

      {(mentions.reddit?.posts?.length > 0 || mentions.hackernews?.posts?.length > 0 || mentions.news?.articles?.length > 0) && (
        <div className="mentions-list">
          <h3>Recent Mentions</h3>
          
          {mentions.reddit?.posts?.length > 0 && (
            <div className="mention-section">
              <h4>🔴 Reddit</h4>
              <div className="posts-list">
                {mentions.reddit.posts.slice(0, 5).map((post, idx) => (
                  <div key={idx} className="post-item">
                    <div className="post-header">
                      <a href={post.url} target="_blank" rel="noopener noreferrer">
                        {post.title}
                      </a>
                      <span className={`sentiment-badge ${post.sentiment.label}`}>
                        {post.sentiment.label}
                      </span>
                    </div>
                    <div className="post-meta">
                      r/{post.subreddit} • {post.score} upvotes • {post.num_comments} comments
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mentions.hackernews?.posts?.length > 0 && (
            <div className="mention-section">
              <h4>🟠 Hacker News</h4>
              <div className="posts-list">
                {mentions.hackernews.posts.slice(0, 5).map((post, idx) => (
                  <div key={idx} className="post-item">
                    <div className="post-header">
                      <a href={post.url} target="_blank" rel="noopener noreferrer">
                        {post.title}
                      </a>
                      <span className={`sentiment-badge ${post.sentiment.label}`}>
                        {post.sentiment.label}
                      </span>
                    </div>
                    <div className="post-meta">
                      {post.points} points • {post.num_comments} comments
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mentions.news?.articles?.length > 0 && (
            <div className="mention-section">
              <h4>📰 News Articles</h4>
              <div className="posts-list">
                {mentions.news.articles.slice(0, 5).map((article, idx) => (
                  <div key={idx} className="post-item">
                    <div className="post-header">
                      <a href={article.url} target="_blank" rel="noopener noreferrer">
                        {article.title}
                      </a>
                      <span className={`sentiment-badge ${article.sentiment.label}`}>
                        {article.sentiment.label}
                      </span>
                    </div>
                    <div className="post-meta">
                      {article.source} • {new Date(article.published).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StockDetail;

